import { CheckCircle } from "lucide-react";

import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { HubLinkListCopy } from "@components/bluetooth-landing/HubLinkList";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import {
  contactFormPl,
  iotClusterLinks,
  iotKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/iot-landing/shared";

export const title =
  "Systemy IoT na zamówienie - elektronika, firmware, aplikacje i cloud";

export const description =
  "Firma IoT: systemy IoT na zamówienie, elektronika i firmware embedded, urządzenia IoT z aplikacją i chmurą. Polski software house od prototypu po produkcję.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Systemy IoT na zamówienie",
    description,
    "IoT product development",
  ),
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Usługi IoT, elektronika i firmware",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Prototypowanie elektroniki i PCB",
        url: "https://bluesbrackets.com/prototypowanie-elektroniki-i-pcb",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Programowanie firmware embedded",
        url: "https://bluesbrackets.com/firmware-embedded",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "System IoT na zamówienie",
        url: "https://bluesbrackets.com/system-iot-na-zamowienie",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Lokalizatory GPS i czujniki IoT",
        url: "https://bluesbrackets.com/lokalizatory-gps-i-czujniki-iot",
      },
      {
        "@type": "ListItem",
        position: 5,
        name: "Audyt elektroniki i firmware",
        url: "https://bluesbrackets.com/audyt-elektroniki-i-firmware",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Artykuły IoT, elektronika i firmware",
    itemListElement: iotKnowledgeBaseLinks.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.text,
      url: `https://bluesbrackets.com${item.href}`,
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Baza wiedzy",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń projekt",
};

export const heroCopy: HeroCopy = {
  header: "Systemy IoT",
  headerTail: "na zamówienie",
  description:
    "Projektujemy elektronikę, firmware embedded, aplikacje i chmurę dla urządzeń IoT. Polski zespół dla firm, które chcą produkt connected od prototypu po produkcję.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz usługi IoT",
    href: "#uslugi",
  },
  benefits: [
    { text: "Elektronika + firmware + app", Icon: CheckCircle },
    { text: "PCB, OTA, LTE-M / NB-IoT", Icon: CheckCircle },
    { text: "Gotowe na CE i rynek UE", Icon: CheckCircle },
  ],
};

export const introCopy = {
  id: "dla-kogo",
  eyebrow: "Dla kogo",
  heading: "Partner IoT dla firm z produktem embedded",
  paragraphs: [
    "W zapytaniach klientów elektronika, firmware i łączność IoT prawie zawsze idą razem: „prototyp urządzenia z PCB, firmware i dashboardem”. Dlatego zbieramy te usługi w jednym klastrze zamiast rozpraszać je po osobnych silosach.",
    "Pracujemy z producentami, importerami elektroniki, startupami hardware i firmami przemysłowymi. Od schematu i PCB, przez firmware (STM32, ESP32, Nordic nRF, Zephyr), po aplikację, backend i panel monitoringu.",
  ],
};

export const servicesListCopy: HubLinkListCopy = {
  id: "uslugi",
  eyebrow: "Usługi IoT",
  heading: "Landingi usługowe w klastrze IoT, elektronika i firmware",
  intro:
    "Wybierz obszar najbliższy Twojemu zapytaniu — każda strona zbiera typowe scenariusze z leadów: prototyp PCB, firmware, system end-to-end, trackery GPS albo audyt istniejącego rozwiązania.",
  items: [
    {
      href: "/prototypowanie-elektroniki-i-pcb",
      title:
        "Prototypowanie elektroniki i PCB - od schematu do działającego urządzenia",
      description:
        "Projektowanie PCB, dobór komponentów i prototyp urządzenia elektronicznego — od schematu po działający PoC gotowy do testów.",
    },
    {
      href: "/firmware-embedded",
      title: "Programowanie firmware embedded - od PoC po produkcję",
      description:
        "Firmware embedded: STM32, ESP32, Nordic nRF, Zephyr, LTE-M / NB-IoT oraz aktualizacje OTA/DFU pod produkcję.",
    },
    {
      href: "/system-iot-na-zamowienie",
      title:
        "System IoT na zamówienie - urządzenie, aplikacja, backend i dashboard",
      description:
        "Kompletna platforma IoT dla firm: urządzenie, firmware, aplikacja mobilna, backend i panel administracyjny.",
    },
    {
      href: "/lokalizatory-gps-i-czujniki-iot",
      title:
        "Lokalizatory GPS i czujniki IoT - trackery, sensory i dashboard",
      description:
        "GPS tracker, czujniki IoT i dashboard monitoringu — urządzenie z panelem w jednym projekcie, nie trzy osobne usługi.",
    },
    {
      href: "/audyt-elektroniki-i-firmware",
      title:
        "Audyt elektroniki i firmware - konsultacje dla produktów embedded",
      description:
        "Niezależny audyt elektroniki, firmware i architektury IoT przed skalowaniem produkcji lub zmianą dostawcy.",
    },
  ],
};

export const blogListCopy: HubLinkListCopy = {
  id: "wartosci",
  eyebrow: "Baza wiedzy",
  heading: "Artykuły wspierające klaster IoT",
  intro:
    "Long-tail treści pod realne zapytania z leadów: koszty urządzenia IoT, wybór łączności LPWA, proces prototypu i porównanie platform embedded.",
  items: [
    {
      href: "/pl/blogs/ile-kosztuje-urzadzenie-iot",
      title: "Ile kosztuje urządzenie IoT? Elektronika, firmware i aplikacja",
      description:
        "Widełki kosztów systemu IoT: PCB, firmware, aplikacja i cloud — kompletny przewodnik wyceny.",
    },
    {
      href: "/pl/blogs/co-to-jest-internet-rzeczy-iot",
      title: "Co to jest IoT? Internet rzeczy w praktyce dla firm",
      description:
        "Czym jest internet rzeczy, jak wygląda architektura produktu IoT i kiedy warto budować własne urządzenie.",
    },
    {
      href: "/pl/blogs/lte-m-vs-nb-iot",
      title: "LTE-M vs NB-IoT - jaką łączność wybrać dla urządzenia IoT?",
      description:
        "Porównanie LTE-M i NB-IoT pod kątem zasięgu, energii, kosztów i typowych scenariuszy produktowych.",
    },
    {
      href: "/pl/blogs/jak-powstaje-prototyp-urzadzenia-elektronicznego",
      title:
        "Jak powstaje prototyp urządzenia elektronicznego? Od schematu do produkcji",
      description:
        "BOM, DFM, produkcja PCB i certyfikacja CE — ścieżka od pomysłu do seryjnego produktu.",
    },
    {
      href: "/pl/blogs/arduino-esp32-stm32-vs-nordic-nrf",
      title: "Arduino, ESP32, STM32 czy Nordic nRF - co wybrać do produktu?",
      description:
        "Kiedy Arduino wystarczy do PoC, a kiedy przejść na ESP32, STM32 albo Nordic nRF w produkcie komercyjnym.",
    },
  ],
};

export const processCopy = {
  id: "proces",
  eyebrow: "Proces",
  heading: "Od konsultacji do działającego produktu IoT",
  steps: [
    {
      title: "Konsultacja i zakres",
      description:
        "Omawiamy urządzenie, łączność, elektronikę i cele biznesowe — oraz ryzyka: bateria, certyfikacja, OTA, dashboard.",
    },
    {
      title: "Prototyp i integracja",
      description:
        "Budujemy działający fragment produktu: PCB lub DevKit, firmware, kluczową komunikację i podgląd danych.",
    },
    {
      title: "Wdrożenie i utrzymanie",
      description:
        "Doprowadzamy urządzenie i oprogramowanie do produkcji, certyfikacji oraz wsparcia po premierze.",
    },
  ],
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Budujesz urządzenie IoT lub produkt embedded?",
    p: "Umów się na darmową konsultację — opowiedz o elektronice, firmware albo systemie end-to-end, a zaproponujemy zakres współpracy.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Jesteśmy firmą IoT: elektronika, firmware embedded, aplikacje i cloud. Działamy z Polski, obsługujemy klientów w UE.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Usługi IoT" },
      { href: "#wartosci", text: "Baza wiedzy" },
      { href: "#proces", text: "Proces współpracy" },
      ...iotClusterLinks.filter(l => l.href !== "/iot"),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy IoT",
    links: [...iotKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
